import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaFacebookF, FaTwitter, FaInstagram, FaYoutube } from "react-icons/fa";
import defaultLogo from "../assets/logo.png";
import { fetchMenu, buildMenuTree, fetchFooterContent, fetchNavbarSettings, fetchSiteIdentity } from "../api";

const pagePath = (slug) => (slug === "home" ? "/" : `/${slug}`);

const fallbackLinks = [
  { id: "home", label: "Home", slug: "home" },
  { id: "contact", label: "Contact", slug: "contact" },
];

const defaultContent = {
  company_name: "Mr. Aali Online Technologies",
  about_text: "Expertise Present Professionals",
  email: "info@mraalionline.com",
  pakistan_phone_1: "+92 608 34 1626",
  pakistan_phone_2: "+92 300 123 1626",
  uae_mobile: "+971 55 637 5452",
  qatar_mobile: "+974 5624 3874",
  facebook_url: "#",
  twitter_url: "#",
  instagram_url: "#",
  youtube_url: "#",
};

const defaultNavbarSettings = {
  login_text: "Login",
  login_url: "#",
  login_visible: true,
};

// A footer link whose target has its own sub-items (Sub-sub Menu): hovering pops up an
// animated card above the link listing them, since the footer sits at the page's bottom edge
// and a downward flyout would run off-screen.
function FooterMenuItem({ item }) {
  const [hovered, setHovered] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <li
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link to={pagePath(item.slug)} className="hover:text-white transition-colors">
        {item.title}
      </Link>
      <AnimatePresence>
        {hasChildren && hovered && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full left-0 mb-2 w-64 bg-navy-dark border border-navy-border rounded-lg shadow-2xl p-3 z-50"
          >
            <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">{item.title}</p>
            <ul className="space-y-1.5">
              {item.children.map((child) => (
                <li key={child.id}>
                  <Link
                    to={pagePath(child.slug)}
                    className="block text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    {child.title}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

export default function Footer() {

  const year = new Date().getFullYear();
  const [tree, setTree] = useState([]);
  const [links, setLinks] = useState(fallbackLinks);
  const [content, setContent] = useState(defaultContent);
  const [navbarSettings, setNavbarSettings] = useState(defaultNavbarSettings);
  const [logoUrl, setLogoUrl] = useState(defaultLogo);

  useEffect(() => {
    fetchMenu()
      .then((items) => {
        setTree(buildMenuTree(items));
        setLinks(
          items
            .filter((item) => !item.parent && item.slug !== "services" && item.slug !== "ai-tools")
            .map((item) => ({ id: item.id, label: item.title, slug: item.slug }))
        );
      })
      .catch(() => {});
    fetchFooterContent().then(setContent).catch(() => {});
    fetchNavbarSettings().then(setNavbarSettings).catch(() => {});
    fetchSiteIdentity().then((identity) => { if (identity.logo) setLogoUrl(identity.logo); }).catch(() => {});
  }, []);

  const services = tree.find((t) => t.slug === "services");
  const aiTools = tree.find((t) => t.slug === "ai-tools");

  return (
    <footer id="contact" className="scroll-mt-20 bg-navy-darker text-gray-300 pt-16 pb-6">

      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* 5 Columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">

          {/* Company Information */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Address</h3>
            <img src={logoUrl} alt={content.company_name} width={180} className="mb-4" />
            <p className="text-sm leading-6">
              {content.about_text} <br/>
              Email: {content.email} <br/><br />
              <b>PAKISTAN</b><br />
              Phone: {content.pakistan_phone_1} <br />
              Phone: {content.pakistan_phone_2} <br />
              <b>UAE</b><br />
              Mobile: {content.uae_mobile} <br />
              <b>QATAR</b><br />
              Mobile: {content.qatar_mobile} <br />
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Services</h3>
            <ul className="space-y-2 text-sm">
              {(services?.children || []).map((item) => (
                <FooterMenuItem key={item.id} item={item} />
              ))}
            </ul>
          </div>

          {/* AI Tools */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">AI Tools</h3>
            <ul className="space-y-2 text-sm">
              {(aiTools?.children || []).map((item) => (
                <FooterMenuItem key={item.id} item={item} />
              ))}
            </ul>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Links</h3>
            <ul className="space-y-2 text-sm">
              {links.map((link) => (
                <li key={link.id}>
                  <Link to={pagePath(link.slug)} className="hover:text-white transition-colors">{link.label}</Link>
                </li>
              ))}
              {navbarSettings.login_visible && (
                <li>
                  {navbarSettings.login_url.startsWith("/") ? (
                    <Link to={navbarSettings.login_url} className="hover:text-white transition-colors">
                      {navbarSettings.login_text}
                    </Link>
                  ) : (
                    <a href={navbarSettings.login_url} className="hover:text-white transition-colors">
                      {navbarSettings.login_text}
                    </a>
                  )}
                </li>
              )}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Follow Us</h3>

            <div className="flex gap-4">

              <motion.a
                whileHover={{ scale: 1.15 }}
                href={content.facebook_url}
                aria-label="Facebook"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-blue-600 transition-colors"
              >
                <FaFacebookF />
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.15 }}
                href={content.twitter_url}
                aria-label="Twitter"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-sky-500 transition-colors"
              >
                <FaTwitter />
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.15 }}
                href={content.instagram_url}
                aria-label="Instagram"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-pink-600 transition-colors"
              >
                <FaInstagram />
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.15 }}
                href={content.youtube_url}
                aria-label="YouTube"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-red-600 transition-colors"
              >
                <FaYoutube />
              </motion.a>

            </div>

          </div>

        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mt-12 pt-6 text-center text-sm text-gray-400">

          © {year} {content.company_name}. All Rights Reserved.

        </div>

      </div>

    </footer>
  );
}
